<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('menu_listing') }
        ]" />
        <h1>{{ $t('menu_listing') }}</h1>
      </header>
      <article class="container framework-slot">
        <div class="form-row toolbar">
            <div class="col col-6 col-md-auto">
                <router-link to="/menus/new" class="btn btn-primary">{{ $t("new_button") }}</router-link>
            </div>
            <div class="col d-none d-md-block"></div>
            <div class="col col-6 col-md-auto text-right">
                <button class="btn btn-outline-secondary" :title="$t('refresh_data')" :disabled="menus.loading"
                        v-on:click="refreshData">
                    <font-awesome-icon :spin="menus.loading" :icon="['fal', 'sync-alt']"/>
                </button>
            </div>
        </div>
        <div v-if="menus.loading">
            <loading-state/>
        </div>
        <div v-else-if="menus.error">
            <request-error-state :try-again-handler="refreshData" :message="menus.error"/>
        </div>
        <div v-else-if="!menus.data.nodes">
            <empty-state/>
        </div>
        <infinite-scroll v-else :handler="queryMoreMenus"
                         :should-handle="menus.data.pageInfo.hasNextPage">
            <div class="row">
                <template v-for="menu in menus.data.nodes">
                    <div :key="menu.id" class="col-12 col-md-6 col-lg-4">
                        <div class="result-entity">
                            <h3>{{menu.name}}</h3>
                            <table class="table table-bordered"></table>
                            <div class="messages alert-danger" v-if="menus.actionErrorMap[menu.id]">
                                {{menus.actionErrorMap[menu.id]}}
                            </div>
                            <div class="actions">
                                <router-link :to="`/menus/${menu.id}/products`"
                                             class="btn btn-sm btn-outline-secondary">
                                    {{ $t('products_button') }}
                                </router-link>
                                <router-link :to="'/menus/' + menu.id"
                                             class="btn btn-sm btn-outline-secondary">
                                    {{ $t('edit_button') }}
                                </router-link>
                                <button class="btn btn-sm btn-outline-danger" @click="deleteMenu(menu)"
                                        :disabled="menus.deletingMap[menu.id]">
                                    {{menus.deletingMap[menu.id] ? $t("deleting") : $t("delete_button")}}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </infinite-scroll>
      </article>
    </app-framework>
</template>

<script>
    import AppFramework from "../../components/layout/AppFramework";
    import InfiniteScroll from "../../components/common/InfiniteScroll";
    import LoadingState from "../../components/states/LoadingState";
    import EmptyState from "../../components/states/EmptyState";
    import RequestErrorState from "../../components/states/RequestErrorState";
    import gql from "graphql-tag";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'menuListing',
        i18nOptions: {namespaces: ['menu_listing', 'common', 'breadcrumbs']},
        components: {BreadCrumbs, RequestErrorState, EmptyState, LoadingState, InfiniteScroll, AppFramework},
        metaInfo() {
          return {
            title: this.$t('menu_listing')
          }
        },
        data: () => ({
            filter: {
                textSearch: null,
                menuId: null
            },
            form: {
                textSearch: "",
                active: true,
                menuId: null
            },
            menus: {
                data: null,
                loading: true,
                error: null,
                deletingMap: {},
                actionErrorMap: {}
            },
        }),
        watch: {
            filter: {
                deep: true,
                handler() {
                    this.queryMenus();
                }
            }
        },
        mounted() {
            this.queryMenus();
        },
        methods: {
            async queryMenus(useCache = true) {
                this.menus.deletingMap = {};
                this.menus.actionErrorMap = {};
                this.menus.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($first: Int! $after: String) {
                            menus(first: $first after: $after) {
                                nodes {
                                    id
                                    name
                                }
                                pageInfo {
                                    hasNextPage
                                    endCursor
                                }
                            }
                        }`,
                        variables: {
                            first: 21,
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.menus.data = {...response.data.menus};
                    this.menus.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.menus.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.menus.error = "Error to load menus";
                    }
                }
                this.menus.loading = false;
            },
            async queryMoreMenus() {
                const response = await this.$apolloClient.query({
                    query: gql`query ($first: Int! $after: String) {
                        menus(first: $first after: $after) {
                            nodes {
                                id
                                name
                            }
                            pageInfo {
                                hasNextPage
                                endCursor
                            }
                        }
                    }`,
                    variables: {
                        first: 21,
                        after: this.menus.data.pageInfo.endCursor
                    },
                    fetchPolicy: "no-cache"
                });
                this.menus.data.nodes = [...this.menus.data.nodes, ...response.data.menus.nodes];
                this.menus.data.pageInfo = response.data.menus.pageInfo;
            },
            async refreshData() {
                await Promise.all([this.queryMenus(false)]);
            },
            async deleteMenu(menu) {
                if (!confirm(this.$t("delete_menu_confirm", {name: menu.name}))) {
                    return
                }
                this.$set(this.menus.deletingMap, menu.id, true);
                this.$set(this.menus.actionErrorMap, menu.id, null);
                try {
                  await this.$apolloClient.mutate({
                    mutation: gql`mutation ($id: ID!) {
                      deleteMenu(
                        id: $id
                      ) {
                        id
                      }
                    }`,
                    variables: {
                      id: menu.id
                    }
                  });
                  const menuIndex = this.menus.data.nodes.indexOf(menu)
                  this.menus.data.nodes.splice(menuIndex, 1)
                  this.$apolloClient.cache.data.set(`Menu:${menu.id}`, undefined)
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.menus.actionErrorMap, menu.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.menus.actionErrorMap, menu.id, "Error deleting record");
                    }
                }
                this.$set(this.menus.deletingMap, menu.id, false);
            }
        }
    }
</script>
